import React from "react";
import type { ApolloClient } from "@apollo/client";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { isEmpty, size } from "lodash";
import { Card } from "react-bootstrap";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import { employerConstants } from "actions/types";
import { FiUserPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import { inviteAdministrator } from "actions/employerActions";
import type { RootState } from "types/state";

import Button from "components/Button";
import IconSpinner from "components/IconSpinner";
import AdministratorTable from "./AdministratorTable";
import AdministratorForm from "./AdministratorForm";
import IconMessageCard from "components/IconMessageCard";

const actions = [employerConstants.GET_ADMINISTRATORS];
const isFetchingSelector = createLoadingSelector(actions);
const isInvitingSelector = createLoadingSelector(
  employerConstants.INVITE_ADMINISTRATOR
);

const isInvitingErrorSelector = createErrorSelector(
  employerConstants.INVITE_ADMINISTRATOR
);

const errorSelector = createErrorSelector(actions);
const mapStateToProps = (state: RootState) => {
  return {
    isFetching: isFetchingSelector(state),
    isInviting: isInvitingSelector(state),
    invitationError: isInvitingErrorSelector(state),
    administrators: state.employer.administrators,
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  inviteAdministrator,
};

// `null` mergeProps (the JS third arg) is react-redux's default — omitting it
// is behavior-identical and the only form the connect() overloads accept.
const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  showAddAdminForm: boolean;
}

class Administrators extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      showAddAdminForm: false,
    };
  }

  inviteAdministrator = (values: Parameters<typeof inviteAdministrator>[1]) => {
    void this.props
      .inviteAdministrator(this.props.client as ApolloClient<object>, values)
      .then((result) => {
        if (result.success) {
          toast.success("Successfully invited administrator.");
          this.setState({ showAddAdminForm: false });
        }
      });
  };

  _adminHeader() {
    const acceptedInvite = this.props.administrators.filter(
      (administrator) => administrator.link.status === "ACCEPTED"
    );

    const numOfActiveAdmins = size(acceptedInvite);

    return (
      <div>
        <div className="widget-header">
          <span>
            <Card.Title>
              Administrators:
              <p className="invite-number">
                Active (<p className="number">{numOfActiveAdmins}</p>)
              </p>
            </Card.Title>
          </span>

          <Button
            icon={{
              icon: <FiUserPlus size={14} color={"#fffff"} />,
              position: "left",
            }}
            size="sm"
            name="action"
            onClick={() => this.setState({ showAddAdminForm: true })}
            btnLabel="Add Admin"
          />
        </div>
      </div>
    );
  }

  _getTable() {
    if (this.props.isFetching) {
      return <IconSpinner centered />;
    }

    if (isEmpty(this.props.administrators)) {
      return (
        <>
          <div style={{ borderTop: "1px solid #f2f4f7" }}>
            <IconMessageCard
              header="Add Administrator"
              subheader="It looks like you haven't invited any administrators yet."
              icon={<FiUserPlus color="white" stroke="#60A4BF" size={16} />}
            />
          </div>
        </>
      );
    }
    return (
      <div className="tab-pane" role="tabpanel" aria-labelledby="account_tab">
        {/* AdministratorTable (sibling, converting this batch) declares a
            tighter local Administrator[] than RootState.employer.administrators;
            cast at the boundary — runtime value is unchanged. */}
        <AdministratorTable
          administrators={
            this.props.administrators as unknown as React.ComponentProps<
              typeof AdministratorTable
            >["administrators"]
          }
        />
      </div>
    );
  }

  _displayContent = () => {
    if (this.state.showAddAdminForm) {
      return (
        <div className="add-employee-form">
          <div className="widget-header">
            <Card.Title>Add Administrators</Card.Title>
          </div>
          <Card.Body>
            <AdministratorForm
              isInviting={this.props.isInviting}
              onSubmit={this.inviteAdministrator}
              error={this.props.invitationError}
              // @ts-expect-error RootState.employer.administrators is looser than AdministratorForm's local Administrator[]; runtime value unchanged — preserved from JS
              administrators={this.props.administrators}
              onCancel={() =>
                this.setState({
                  showAddAdminForm: false,
                })
              }
            />
          </Card.Body>
        </div>
      );
    }
    return (
      <>
        {this._adminHeader()}
        {this._getTable()}
      </>
    );
  };

  render() {
    const content = this._displayContent();
    return (
      <div
        className="tab-pane"
        id="group"
        role="tabpanel"
        aria-labelledby="group_tab"
      >
        <Card className="groups-table">{content}</Card>
      </div>
    );
  }
}

export default connector(withApollo<Props>(Administrators));
